import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#D97757] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold text-stone-900">BeautyPro Review</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center text-stone-600 hover:text-stone-900 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <h1 className="font-heading text-3xl font-semibold text-stone-900 mb-2">
          TERMS OF USE & PROFESSIONAL REVIEW AGREEMENT
        </h1>
        <p className="text-stone-600 mb-8"><strong>Last Updated:</strong> September 16, 2026</p>
        
        <div className="prose prose-stone max-w-none">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8">
            <p className="text-amber-800 font-medium">
              PLEASE READ THESE TERMS OF USE CAREFULLY BEFORE CREATING AN ACCOUNT. BY CHECKING THE BOX, 
              CLICKING &quot;I AGREE,&quot; OR USING THE BEAUTYPRO REVIEW PLATFORM, YOU AGREE TO BE BOUND BY ALL TERMS 
              AND CONDITIONS BELOW. IF YOU DO NOT AGREE, DO NOT ACCESS OR USE THE PLATFORM.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-stone-900 mb-4">
              1. PROFESSIONAL ELIGIBILITY & REGISTRATION
            </h2>
            <p className="text-stone-700 leading-relaxed mb-4">
              By registering as a user on BeautyPro Review (&quot;Platform&quot;), you represent and warrant that:
            </p>
            <ul className="list-none pl-0 text-stone-700 space-y-3">
              <li className="pl-6">
                <strong>(a)</strong> You are a verified professional operating within the beauty, wellness, 
                manicurist, skincare, or barbering industries;
              </li>
              <li className="pl-6">
                <strong>(b)</strong> You are providing information that is accurate, current, and complete;
              </li>
              <li className="pl-6">
                <strong>(c)</strong> You are using the Platform solely for professional, non-malicious evaluation 
                and operational logging regarding client interactions.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-stone-900 mb-4">
              2. CLIENT REVIEW GUIDELINES & DEFAMATION PROHIBITION
            </h2>
            <p className="text-stone-700 leading-relaxed mb-4">
              The Platform allows professionals to submit feedback and reviews regarding client interactions 
              (&quot;Client Reviews&quot;). You expressly agree that all Client Reviews submitted by you shall strictly 
              adhere to the following rules:
            </p>
            <ul className="list-none pl-0 text-stone-700 space-y-3">
              <li className="pl-6">
                <strong>(a) Truthfulness and Firsthand Knowledge:</strong> Every review must be based on a genuine, 
                completed, direct professional service or booking interaction. You shall not submit fake, exaggerated, 
                or speculative reviews.
              </li>
              <li className="pl-6">
                <strong>(b) Prohibition of Defamation and Libel:</strong> You are strictly prohibited from publishing 
                false statements, slanderous claims, libelous accusations, or malicious attacks against any 
                individual&apos;s character or business reputation.
              </li>
              <li className="pl-6">
                <strong>(c) Neutral and Objective Language:</strong> Reviews must maintain a professional tone focused 
                strictly on scheduling, payment integrity, behavior, safety concerns, or professional service suitability.
              </li>
              <li className="pl-6">
                <strong>(d) Prohibition of Harassment and Discrimination:</strong> Reviews must not contain hate speech, 
                discriminatory remarks, threats, profanity, or sexually explicit content.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-stone-900 mb-4">
              3. CLIENT PRIVACY & PROTECTED INFORMATION
            </h2>
            <p className="text-stone-700 leading-relaxed mb-4">
              You agree to protect client privacy at all times. When submitting any review, note, or media:
            </p>
            <ul className="list-none pl-0 text-stone-700 space-y-3">
              <li className="pl-6">
                <strong>(a) No Personally Identifiable Information (PII):</strong> You shall not disclose a client&apos;s 
                home address, personal phone number, social security number, financial details, or exact employer.
              </li>
              <li className="pl-6">
                <strong>(b) Medical & Sensitive Data:</strong> You shall not record or publish medical conditions, 
                dermatological diagnoses, or protected health information.
              </li>
              <li className="pl-6">
                <strong>(c) Client Imagery:</strong> You warrant that you have obtained all necessary permissions and 
                releases from a client before uploading any photographs, videos, or imagery containing that client&apos;s 
                face, hair, or physical body.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-stone-900 mb-4">
              4. CONTENT MONITORING, TERMINATION, AND REMOVAL
            </h2>
            <p className="text-stone-700 leading-relaxed mb-4">
              BeautyPro Review acts as a passive conduit for user-generated content. While BeautyPro Review has no 
              obligation to screen all Client Reviews prior to publication, BeautyPro Review reserves the right, 
              in its sole discretion, to:
            </p>
            <ul className="list-none pl-0 text-stone-700 space-y-3">
              <li className="pl-6">
                <strong>(a)</strong> Monitor, edit, redact, or remove any Client Review that violates these Terms 
                or is reported as abusive;
              </li>
              <li className="pl-6">
                <strong>(b)</strong> Immediately suspend or permanently revoke account access for any professional 
                who posts false, defamatory, or privacy-violating content, without refund or prior notice.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-stone-900 mb-4">
              5. INDEMNIFICATION (YOUR RESPONSIBILITY FOR LEGAL CLAIMS)
            </h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 leading-relaxed mb-4 font-medium">
                YOU AGREE TO INDEMNIFY, DEFEND, AND HOLD HARMLESS BEAUTY PRO REVIEW, ITS OFFICERS, DIRECTORS, 
                EMPLOYEES, AND AGENTS FROM AND AGAINST ANY AND ALL CLAIMS, LIABILITIES, DAMAGES, LOSSES, COSTS, 
                OR EXPENSES (INCLUDING REASONABLE ATTORNEY&apos;S FEES) ARISING OUT OF OR IN ANY WAY CONNECTED WITH:
              </p>
              <ul className="list-none pl-0 text-red-800 space-y-3">
                <li className="pl-6">
                  <strong>(A)</strong> ANY CLIENT REVIEW OR CONTENT YOU SUBMIT TO THE PLATFORM;
                </li>
                <li className="pl-6">
                  <strong>(B)</strong> ANY CLAIM OF DEFAMATION, LIBEL, SLANDER, PRIVACY VIOLATION, OR INTELLECTUAL 
                  PROPERTY INFRINGEMENT BROUGHT BY A THIRD PARTY (INCLUDING A CLIENT) REGARDING YOUR SUBMISSIONS; OR
                </li>
                <li className="pl-6">
                  <strong>(C)</strong> YOUR VIOLATION OF THESE TERMS OF USE.
                </li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-stone-900 mb-4">
              6. LIMITATION OF LIABILITY
            </h2>
            <p className="text-stone-700 leading-relaxed uppercase font-medium">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, BEAUTY PRO REVIEW SHALL NOT BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES ARISING FROM OR RELATED TO YOUR USE OF THE 
              PLATFORM OR CONTENT POSTED BY OTHER USERS.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-stone-900 mb-4">
              7. GOVERNING LAW
            </h2>
            <p className="text-stone-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of California, 
              United States of America, without regard to its conflict of law principles.
            </p>
          </section>

          <section className="mt-12 p-6 bg-stone-100 rounded-lg">
            <p className="text-stone-700 leading-relaxed">
              For questions about these terms, contact us at{' '}
              <a href="mailto:info@beautyproreview.com" className="text-[#D97757] hover:underline">
                info@beautyproreview.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
